import { Alert, Button, Empty, Segmented, Space, Spin, Typography } from "antd";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useState } from "react";
import { VolunteerStore } from "./store";
import { SortableSubjectItem } from "./components/SortableSubjectItem";
import { SubjectGroup } from "./components/SubjectGroup";
import { TaskFormModal } from "./components/TaskFormModal";
import { SubjectFormModal } from "./components/SubjectFormModal";
import { TimeScheduleView } from "./components/TimeScheduleView";
import { StackRow, wrapText } from "./components/responsive";
import { useOpenDashServices } from "@opendash/core";

export const VolunteerPage = observer(() => {
  const store = useMemo(() => new VolunteerStore(), []);
  const { UserService } = useOpenDashServices();
  const isAdmin = UserService.isLoggedIn();
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [addSubjectOpen, setAddSubjectOpen] = useState(false);
  const [view, setView] = useState<"time" | "tasks">("time");
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  useEffect(() => {
    store.loadAll();
  }, [store]);

  const subjectGroups = store.taskGroups.filter((g) => g.subject !== null);
  const unassignedGroup = store.taskGroups.find((g) => g.subject === null);

  const handleDragEndSubjects = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = subjectGroups.map((g) => g.subject!.objectId!);
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    store.reorderSubjects(arrayMove(ids, oldIndex, newIndex));
  };

  if (store.loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (store.error) {
    return <Alert type="error" message="Fehler beim Laden" description={store.error} style={{ margin: 24 }} />;
  }

  return (
    <div style={{ padding: "16px 24px" }}>
      <StackRow style={{ marginBottom: 24 }}>
        <Typography.Title level={3} style={{ margin: 0, ...wrapText }}>
          Helfer:innen gesucht
        </Typography.Title>
          <Space wrap>
            <Segmented
              options={[
                { label: "Zeitplan", value: "time" },
                { label: "Aufgaben", value: "tasks" },
              ]}
              value={view}
              onChange={(v) => setView(v as "time" | "tasks")}
            />
        {isAdmin ? (
          <>
            <Button onClick={() => setAddSubjectOpen(true)}>Neues Thema</Button>
            <Button type="primary" onClick={() => setAddTaskOpen(true)}>
              Neue Aufgabe
              </Button>
          </>
        ) : (
          <Button
            type="link"
            href={"/auth/login?url=" + encodeURIComponent(window.location.pathname + window.location.search)}
          >
            Admin
          </Button>
        )}
          </Space>
      </StackRow>

      {view === "time" ? (
        <TimeScheduleView store={store} isAdmin={isAdmin} />
      ) : store.taskGroups.length === 0 ? (
        <Empty description="Noch keine Aufgaben veröffentlicht" />
      ) : isAdmin ? (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEndSubjects}
          >
            <SortableContext
              items={subjectGroups.map((g) => g.subject!.objectId!)}
              strategy={verticalListSortingStrategy}
            >
              {subjectGroups.map((group) => (
                <SortableSubjectItem
                  key={group.subject!.objectId}
                  store={store}
                  subject={group.subject!}
                  tasks={group.tasks}
                  isAdmin={isAdmin}
                />
              ))}
            </SortableContext>
          </DndContext>

          {unassignedGroup && (
            <SubjectGroup
              store={store}
              subject={null}
              tasks={unassignedGroup.tasks}
              isAdmin={isAdmin}
            />
          )}
        </>
      )
            : (
              <>
                {subjectGroups.map((group) => (
                  <SortableSubjectItem
                    key={group.subject!.objectId}
                    store={store}
                    subject={group.subject!}
                    tasks={group.tasks}
                    isAdmin={isAdmin}
                  />
                ))}
                {unassignedGroup && (
                  <SubjectGroup
                    store={store}
                    subject={null}
                    tasks={unassignedGroup.tasks}
                    isAdmin={isAdmin}
                  />
                )}
              </>
            )}

      {isAdmin && (
        <>
          <TaskFormModal store={store} open={addTaskOpen} onClose={() => setAddTaskOpen(false)} />
          <SubjectFormModal store={store} open={addSubjectOpen} onClose={() => setAddSubjectOpen(false)} />
        </>
      )}
    </div>
  );
});
